import map from "lodash/map";
import isEmpty from "lodash/isEmpty";
import { extractCorePost } from "../../utils/posts";
import { hasVideoExtension } from "../../utils/media";
import { POST_TYPES } from "../../constants/PostsView";
import type { LoadedDraft, Post } from "../../shared/types/post";
import type { AccountType } from "../../shared/types/accounts";

type PostWithLegacyId = {
  id?: string;
};

/**
 * Seeds a post form from a stored post. Kept out of postFormsReducer so code
 * that only needs the seeding (Post now in the posts dropdown) does not build
 * the reducer at import time.
 */
export const buildInitialPost = (
  post: Post | LoadedDraft,
  accounts: AccountType[]
) => {
  const normalizedPost = extractCorePost(post);
  const accountIds = map(accounts, "_id");

  const postCopy = {
    piBoards: [],
    ...normalizedPost,
    isStory:
      normalizedPost.isStory === true ||
      normalizedPost.type === POST_TYPES.STORY,
    accounts: accounts,
    accountIds: accountIds,
  };

  // because with drafts, post.schedule will not contain the ID
  if (!postCopy._id) {
    const postId = post._id || (post as PostWithLegacyId).id;
    if (postId) {
      postCopy._id = postId;
    }
  }

  delete postCopy.postState;

  if (
    normalizedPost.when &&
    typeof normalizedPost.when === "string" &&
    normalizedPost.when.toLowerCase() === "invalid date"
  ) {
    postCopy.when = null;
  }

  // We use both of these but the previewer relies on originalUrl
  if (postCopy.origUrl && !postCopy.originalUrl) {
    postCopy["originalUrl"] = postCopy.origUrl;
  }

  // Some story posts don't set original url
  if (!postCopy.origUrl && !postCopy.originalUrl && postCopy.url) {
    postCopy["originalUrl"] = postCopy.url;
    postCopy["origUrl"] = postCopy.url;
  }

  if (
    isEmpty(postCopy.mediaFiles) && // There is a bug where a story that has a single video doesn't set isVideo
    postCopy.isStory &&
    hasVideoExtension(postCopy.originalUrl)
  ) {
    postCopy.isVideo = true;
  }

  // Populate videoCoverPreview from thumbnailUrl when loading a draft with a custom cover
  // videoCoverPreview is not persisted to the API, but thumbnailUrl is
  if (
    !postCopy.videoCoverPreview &&
    postCopy.thumbnailUrl &&
    postCopy.videoCoverImageTimestamp !== undefined &&
    postCopy.videoCoverImageTimestamp !== 0
  ) {
    postCopy.videoCoverPreview = postCopy.thumbnailUrl;
  }

  return postCopy;
};
